import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGetDevice } from '../../../../api/routes/devices'
import {
    useGetDeviceSettings,
    useUpdateDeviceSettings,
} from '../../../../api/routes/device-settings'
import { authClient, hasPermission } from '../../../../lib/auth-client'
import { useBusinessModuleCheck } from '../../../../api/routes/modules'
import { Button } from '../../../../components/ui/button'
import Header from '../header'
import SettingsField from './settings-field'
import { toast } from 'react-toastify'

const DeviceSettingsRoute = () => {
    const { id: deviceId } = useParams()
    const { data: device, isLoading: deviceLoading } = useGetDevice(deviceId as string)
    const { data: moduleAccess, isLoading: moduleLoading } = useBusinessModuleCheck(
        device?.business?.id || null,
        'device-config'
    )
    const hasDeviceConfig = !!moduleAccess?.hasAccess

    const { data: settings, isLoading: settingsLoading } = useGetDeviceSettings(hasDeviceConfig ? (deviceId as string) : '')
    const updateSettings = useUpdateDeviceSettings()
    const { data: session, isPending: sessionLoading } = authClient.useSession()
    const user = session?.user as any

    const [isEditing, setIsEditing] = useState(false)
    const [editValues, setEditValues] = useState<Record<string, any>>({})

    const isLoading = deviceLoading || sessionLoading || moduleLoading || (hasDeviceConfig ? settingsLoading : false)

    if (isLoading || !user) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 w-48 bg-muted rounded" />
                    <div className="h-10 w-full sm:w-96 bg-muted rounded-lg" />
                    <div className="h-64 bg-muted rounded-lg" />
                </div>
            </div>
        )
    }

    if (!device || !deviceId) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <p className="text-muted-foreground">Device not found</p>
                </div>
            </div>
        )
    }

    if (!hasDeviceConfig) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <Header deviceId={deviceId} name={device?.name} businessId={device?.business?.id} />
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <p className="text-sm font-medium text-foreground mb-1">Module not available</p>
                    <p className="text-xs text-muted-foreground">
                        The Device Configuration module is not enabled for your business.
                        Contact your administrator to enable this feature.
                    </p>
                </div>
            </div>
        )
    }

    const settingsList = settings || []

    const handleStartEdit = () => {
        const initial: Record<string, any> = {}
        settingsList.forEach((s: any) => {
            initial[s.templateId] = s.value
        })
        setEditValues(initial)
        setIsEditing(true)
    }

    const handleCancel = () => {
        setIsEditing(false)
        setEditValues({})
    }

    const handleSave = () => {
        const payload = Object.entries(editValues).map(([templateId, value]) => ({
            templateId,
            value,
        }))
        updateSettings.mutate(
            {
                deviceId: deviceId as string,
                data: payload,
            },
            {
                onSuccess: () => {
                    toast.success('Settings updated')
                    setIsEditing(false)
                    setEditValues({})
                },
                onError: (error: any) => {
                    toast.error(
                        error?.response?.data?.message ||
                            error.message ||
                            'Failed to update settings'
                    )
                },
            }
        )
    }

    const handleFieldChange = (templateId: string, value: any) => {
        setEditValues((prev) => ({ ...prev, [templateId]: value }))
    }

    // Group settings by group field (API returns flat objects with group, not nested template)
    const groups: Record<string, any[]> = {}
    settingsList.forEach((s: any) => {
        const group = s.group || 'General'
        if (!groups[group]) groups[group] = []
        groups[group].push(s)
    })

    // Sort within groups by sortOrder
    Object.values(groups).forEach((items) => {
        items.sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    })

    // Find most recent updatedAt
    const lastUpdated = settingsList.reduce((latest: any, s: any) => {
        if (!latest || (s.updatedAt && new Date(s.updatedAt) > new Date(latest.updatedAt))) {
            return s
        }
        return latest
    }, null)

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <Header deviceId={deviceId} name={device?.name} businessId={device?.business?.id} />

            {settingsList.length === 0 ? (
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <p className="text-muted-foreground">
                        No configuration templates have been defined for this device type.
                    </p>
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                            Device Settings
                        </h2>
                        {hasPermission(user.role, { device: ['update'] }) && (
                            <div className="flex gap-2">
                                {isEditing ? (
                                    <>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={handleSave}
                                            disabled={updateSettings.isPending}
                                        >
                                            {updateSettings.isPending ? 'Saving...' : 'Save'}
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleStartEdit}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                    {Object.entries(groups).map(([groupName, items]) => (
                        <div key={groupName} className="rounded-lg border bg-card mb-6">
                            <div className="px-6 py-4 border-b">
                                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                                    {groupName}
                                </h3>
                            </div>
                            <div className="divide-y divide-border">
                                {items.map((setting: any) => (
                                    <div
                                        key={setting.templateId}
                                        className="flex items-center justify-between px-6 py-4"
                                    >
                                        <div className="space-y-0.5">
                                            <p className="text-sm font-medium text-foreground">
                                                {setting.label || setting.name}
                                            </p>
                                            {setting.description && (
                                                <p className="text-xs text-muted-foreground">
                                                    {setting.description}
                                                </p>
                                            )}
                                        </div>
                                        <div className="min-w-[200px] flex justify-end">
                                            <SettingsField
                                                template={setting}
                                                value={
                                                    isEditing
                                                        ? editValues[setting.templateId]
                                                        : setting.value
                                                }
                                                isEditing={isEditing}
                                                onChange={(val) =>
                                                    handleFieldChange(setting.templateId, val)
                                                }
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {lastUpdated?.updatedAt && (
                        <p className="text-xs text-muted-foreground mt-2">
                            Last updated{' '}
                            {new Date(lastUpdated.updatedAt).toLocaleString()}
                            {lastUpdated.updatedBy && ` by ${lastUpdated.updatedBy.name || lastUpdated.updatedBy}`}
                        </p>
                    )}
                </>
            )}
        </div>
    )
}

export { DeviceSettingsRoute }
